import type { ReactNode } from "react"
import { BidMetadataStrip } from "@/components/BidMetadataStrip"
import { BidActionsMenu } from "@/components/BidActionsMenu"
import { BidStageControl } from "@/components/bid-lifecycle/BidStageControl"
import type { Bid } from "@/lib/schemas"
import type { BidMemberSummary } from "@/stores/bid-members-store"

interface BidHeaderProps {
  bid: Bid
  members?: BidMemberSummary[] | undefined
  readOnly: boolean
  /** Page-specific actions, left of the ⋯ menu (e.g. "Analyse bid"). */
  actions?: ReactNode
  onEdited?: (() => void) | undefined
  onDeleted?: (() => void) | undefined
}

/**
 * The bid header (R4): on every bid page except the editors, which keep their
 * own. Today's metadata strip and ⋯ menu, moved off Overview-only, plus the
 * stage control (viewers see the stage pill only). Pinned
 * above the page's own scroll area (design system: sticky sub-header).
 */
export function BidHeader({ bid, members, readOnly, actions, onEdited, onDeleted }: BidHeaderProps) {
  return (
    <div
      data-testid="bid-header"
      className="sticky top-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-border bg-background px-6 py-3"
    >
      <div className="flex min-w-0 max-w-full flex-[1_1_20rem] flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="min-w-0 max-w-[28rem] truncate text-sm font-medium text-foreground" title={bid.title}>
          {bid.title}
        </h1>
        <BidMetadataStrip bid={bid} members={members ?? []} />
      </div>
      <div className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-2">
        <BidStageControl bid={bid} readOnly={readOnly} />
        {actions}
        {!readOnly && (
          <BidActionsMenu bid={bid} onEdited={onEdited ?? (() => {})} onDeleted={onDeleted ?? (() => {})} />
        )}
      </div>
    </div>
  )
}
